import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Load image and convert to base64
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (error) => {
      console.error("Failed to load image:", imagePath, error);
      reject(error);
    };
    img.src = `${process.env.PUBLIC_URL}${imagePath}`;
  });
};

/**
 * Generate SCF Annexure PDF – one record per page, title on every page, correct page numbering, margin below title
 */
export const generateSCFAnnexure = async (
  certificateData = {},
  scfData = [],
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = 210;
  const pageHeight = 297;

  try {
    if (!scfData || scfData.length === 0) {
      console.warn("No SCF data found");
      return {
        success: false,
        message: "No SCF data found for this application",
      };
    }

    // Load images
    let headerImage, logoImage;

    try {
      headerImage = await loadImageAsBase64(
        "/certificate_images/scopeCrystalnew1.png",
      );
      console.log("✓ Header image loaded for SCF Annexure");
    } catch (err) {
      console.warn("✗ Header image not found for SCF Annexure");
    }

    try {
      logoImage = await loadImageAsBase64(
        "/certificate_images/certificate_logo.png",
      );
      console.log("✓ Logo image loaded for SCF Annexure");
    } catch (err) {
      console.warn("✗ Logo image not found for SCF Annexure");
    }

    // Helper to remove duplicate address parts
    const removeDuplicateAddressParts = (address) => {
      if (!address) return '';
      const parts = address.split(',').map(part => part.trim());
      const cleaned = [];
      parts.forEach(part => {
        const upperPart = part.toUpperCase();
        if (
          cleaned.length === 0 ||
          cleaned[cleaned.length - 1].toUpperCase() !== upperPart
        ) {
          cleaned.push(part);
        }
      });
      return cleaned.join(', ');
    };

    const cleanedAddress = removeDuplicateAddressParts(certificateData.address);

    // Table columns definition
    const tableColumn = [
      "Sno.",
      "ID of Sample Collection Center / Facility",
      "Name of Sample Collection Center / Facility,Location",
      "Address of Sample Collection Center /Facility",
    ];

    // Total pages = number of SCF records
    const totalPages = scfData.length;

    // Start Y for title section (after header image and lab details)
    const titleStartY = headerImage ? 105 : 60;

    // ─── Define a function that returns the page decoration callback with a fixed page number ───
    const createPageDecorations = (pageNumber) => {
      return function (pageData) {
        // We ignore pageData.pageNumber and use the captured pageNumber
        const currentPage = pageNumber;

        // Watermark logo
        if (logoImage) {
          try {
            doc.saveGraphicsState();
            doc.setGState(new doc.GState({ opacity: 0.5 }));
            const watermarkSize = 150;
            const watermarkX = (pageWidth - watermarkSize) / 2;
            const watermarkY = (pageHeight - watermarkSize) / 2;
            doc.addImage(
              logoImage,
              "PNG",
              watermarkX,
              watermarkY,
              watermarkSize,
              watermarkSize,
            );
            doc.restoreGraphicsState();
          } catch (err) {
            console.error("Failed to add watermark:", err);
          }
        }

        // Header image at top
        if (headerImage) {
          const headerWidth = doc.internal.pageSize.getWidth() - 20;
          const headerHeight = 38;
          const headerX = (pageWidth - headerWidth) / 2;
          doc.addImage(
            headerImage,
            "PNG",
            headerX,
            5,
            headerWidth,
            headerHeight,
          );
        }

        const headerStartY = headerImage ? 43 : 10;
        const leftMargin = 5;
        const rightMargin = 5;
        const rowHeight = 7;
        let yPos = headerStartY;

        doc.setFontSize(10.5);

        // Laboratory Name (with wrapping)
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Laboratory Name :', leftMargin + 2, yPos + 5);

        doc.setFont('helvetica', 'normal');
        const labText = `${certificateData.labName}${cleanedAddress ? ', ' + cleanedAddress : ''}`;
        const maxWidth = pageWidth - (leftMargin + 55) - rightMargin;
        const wrappedText = doc.splitTextToSize(labText, maxWidth);
        doc.text(wrappedText, leftMargin + 55, yPos + 5);
        yPos += wrappedText.length * 6;

        // Accreditation Standard
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Accreditation Standard :", leftMargin + 2, yPos + 5);
        doc.setFont("helvetica", "normal");
        doc.text(
          certificateData.standard || "",
          leftMargin + 55,
          yPos + 5,
        );
        yPos += rowHeight;

        // Certificate Number
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Certificate Number :", leftMargin + 2, yPos + 5);
        doc.setFont("helvetica", "normal");
        doc.text(
          certificateData.certificateNumber || "",
          leftMargin + 55,
          yPos + 5,
        );
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Page No :", leftMargin + 122, yPos + 5);
        doc.setFont("helvetica", "normal");
        // Use captured currentPage and totalPages
        doc.text(`${currentPage} of ${totalPages}`, leftMargin + 142, yPos + 5);
        yPos += rowHeight;

        // Validity
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Validity :", leftMargin + 2, yPos + 5);
        doc.setFont("helvetica", "normal");
        doc.text(
          `${certificateData.issueDate && certificateData.validUntil ? `${certificateData.issueDate} to ${certificateData.validUntil}` : ""}`,
          leftMargin + 55,
          yPos + 5,
        );

        // Last Amended on
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Last Amended on :", leftMargin + 122, yPos + 5);
        doc.setFont("helvetica", "normal");
        doc.text(
          certificateData.amendmentDate || "",
          leftMargin + 162,
          yPos + 5,
        );

        // ─── Draw the main title on every page (with borders) ───
        const titleY = titleStartY;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        const titleText = "Recognised Sample Collection Center/Facility";
        const titleWidth = doc.getTextWidth(titleText);
        const titleX = (pageWidth - titleWidth) / 2;

        doc.setLineWidth(0.5);
        doc.line(5, titleY - 8, pageWidth - 5, titleY - 8);
        doc.text(titleText, titleX, titleY - 3);
        doc.line(5, titleY, pageWidth - 5, titleY);

        // Footer
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const footerText =
          "This is annexure to 'Certificate of Accreditation' and does not require any signature.";
        doc.text(footerText, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      };
    };

    // ─── Loop through SCF records, one per page ───
    for (let idx = 0; idx < scfData.length; idx++) {
      const scf = scfData[idx];

      // Add a new page after the first record
      if (idx > 0) {
        doc.addPage();
      }

      // Table with single row for this record
      const tableStartY = titleStartY + 5; // 5mm margin below title

      const tableRows = [[
        idx + 1,
        scf.scfid || "",
        scf.name || "",
        scf.address || "",
      ]];

      // Create a page decorations callback with the correct page number
      const pageNumber = idx + 1;
      const pageDecorations = createPageDecorations(pageNumber);

      autoTable(doc, {
        startY: tableStartY,
        head: [tableColumn],
        body: tableRows,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 1,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          overflow: "linebreak",
          cellWidth: "wrap",
          fillColor: false,
          textColor: [0, 0, 0],
          halign: "left",
          valign: "middle",
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontSize: 8,
          fontStyle: "bold",
          halign: "left",
          valign: "middle",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          fontSize: 9,
          fontStyle: "normal",
        },
        columnStyles: {
          0: { cellWidth: 18, halign: "center", valign: "middle" },
          1: { cellWidth: 52, halign: "left", valign: "middle" },
          2: { cellWidth: 65, halign: "left", valign: "middle" },
          3: { cellWidth: 65, halign: "left", valign: "middle" },
        },
        margin: { left: 5, right: 5, bottom: 30, top: 0 },
        pageBreak: 'none',
        didDrawPage: pageDecorations,
      });
    }

    // Generate filename with timestamp
    const now = new Date();
    const timestamp =
      now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");

    const fileName = `SCF_Annexure_${certificateData.certificateNumber}-${timestamp}.pdf`;

    const pdfBlob = doc.output('blob');
    console.log("✓ SCF Annexure PDF generated successfully");

    return { success: true, message: "SCF Annexure generated successfully", pdfBlob, fileName, doc };

  } catch (error) {
    console.error("Error generating SCF Annexure:", error);
    throw new Error(`Failed to generate SCF Annexure: ${error.message}`);
  }
};

export default {
  generateSCFAnnexure,
};
